// Pure helpers behind the Competitor Detail drawer.
//
// `fn_get_aim_competitor_detail` returns a run summary with both grains on it:
//
//   { competitor, total_mentions, providers_mentioning, avg_position,
//     best_position, superlative_mentions, warning_mentions,
//     by_provider: [{ provider, mentions, avg_position, strengths[] }],
//     mentions:    [{ provider, prompt_category, prompt_text, list_position,
//                     recommendation_strength, … }],
//     scope:       { label, from, to, run_ids[], runs_considered },
//     by_run:      [{ run_id, run_at, mentions }] }
//
// `scope` names the window of runs every count covers — the same one the competitor table is
// anchored to — and `by_run` splits the total across those runs.
//
// The Express query it replaced returned the per-mention rows as a bare array,
// keyed `display_name` / `category`. Both are recognised.

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Does this payload look like the Express bare per-mention array? */
const isMentionRows = (payload) => Array.isArray(payload)
  && payload.some((r) => r && typeof r === 'object'
    && ('prompt_text' in r || 'list_position' in r || 'display_name' in r));

/** Unwrap a one-element array envelope, tolerating a bare object. */
const unwrap = (payload) => {
  if (Array.isArray(payload)) return payload.length === 1 && payload[0] && typeof payload[0] === 'object'
    ? payload[0]
    : null;
  return payload && typeof payload === 'object' ? payload : null;
};

/**
 * Normalize one mention onto the fields the drawer's table reads.
 *
 * The RPC names the provider `provider` and the query category `prompt_category`;
 * the Express query named them `display_name` and `category`. Either is accepted
 * so the drawer reads one shape.
 *
 * @param {object} row a per-mention row
 * @returns {object} the row with `display_name`, `category` and `list_position` set
 */
function normalizeMention(row) {
  return {
    ...row,
    display_name: row.display_name ?? row.provider ?? '',
    category: row.category ?? row.prompt_category ?? '',
    list_position: toNumberOrNull(row.list_position),
  };
}

/**
 * Normalize the window the counts cover.
 *
 * @param {*} scope the payload's `scope`
 * @returns {{label: string, from: string|null, to: string|null, run_ids: string[]}|null}
 *   null unless the payload names the window with a label
 */
function normalizeScope(scope) {
  if (!scope || typeof scope !== 'object' || typeof scope.label !== 'string' || !scope.label) return null;
  return {
    label: scope.label,
    from: scope.from ?? null,
    to: scope.to ?? null,
    run_ids: Array.isArray(scope.run_ids) ? scope.run_ids : [],
  };
}

/**
 * Normalize the per-run split of the total, newest run first.
 *
 * @param {*} byRun the payload's `by_run`
 * @returns {Array<{run_id: string, run_at: string|null, mentions: number|null}>}
 */
function normalizeByRun(byRun) {
  return (Array.isArray(byRun) ? byRun : [])
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({ ...r, run_at: r.run_at ?? null, mentions: toNumberOrNull(r.mentions) }))
    .sort((a, b) => String(b.run_at ?? '').localeCompare(String(a.run_at ?? '')));
}

/**
 * Decide what the drawer can render from whatever the endpoint returned.
 *
 * Per-mention rows win over the per-provider aggregate when both are present:
 * they carry the query category and prompt text the aggregate cannot express,
 * and the drawer shows the aggregate's headline stats above either table.
 *
 * @param {Array|object|null|undefined} payload the endpoint payload
 * @returns {{mode: 'mentions'|'providers'|'empty', mentions: Array<object>,
 *   providers: Array<object>, summary: object|null, scope: object|null,
 *   byRun: Array<object>}} `mode` tells the drawer which table to draw.
 *   `summary` is present whenever the payload carries one; `scope` and `byRun`
 *   whenever it names its window, even when nothing in it mentions the competitor.
 */
export function competitorDetailView(payload) {
  const empty = { mode: 'empty', mentions: [], providers: [], summary: null, scope: null, byRun: [] };

  if (isMentionRows(payload)) {
    const mentions = payload.filter((r) => r && typeof r === 'object').map(normalizeMention);
    return mentions.length
      ? { ...empty, mode: 'mentions', mentions }
      : empty;
  }

  const agg = unwrap(payload);
  if (!agg) return empty;
  const scope = normalizeScope(agg.scope);
  const byRun = normalizeByRun(agg.by_run);

  const mentions = (Array.isArray(agg.mentions) ? agg.mentions : [])
    .filter((r) => r && typeof r === 'object')
    .map(normalizeMention);

  const providers = (Array.isArray(agg.by_provider) ? agg.by_provider : [])
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      ...r,
      provider: r.provider ?? r.display_name ?? '',
      mentions: toNumberOrNull(r.mentions),
      avg_position: toNumberOrNull(r.avg_position),
      strengths: Array.isArray(r.strengths) ? r.strengths : [],
    }))
    .sort((a, b) => (b.mentions ?? 0) - (a.mentions ?? 0));

  const summary = {
    total_mentions: toNumberOrNull(agg.total_mentions),
    providers_mentioning: toNumberOrNull(agg.providers_mentioning),
    avg_position: toNumberOrNull(agg.avg_position),
    superlative_mentions: toNumberOrNull(agg.superlative_mentions),
    warning_mentions: toNumberOrNull(agg.warning_mentions),
  };
  const hasSummary = Object.values(summary).some((v) => v != null && v !== 0);

  if (mentions.length) return { mode: 'mentions', mentions, providers, summary, scope, byRun };
  if (providers.length || hasSummary) return { mode: 'providers', mentions: [], providers, summary, scope, byRun };
  return { ...empty, scope, byRun };
}
